import { useQuery } from '@tanstack/react-query';
import { API } from 'api';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';

import { apiBasePath, apiName } from 'assets';
import { Color } from '@repo/ui/components/GaugeChart/getConfig.js';

export interface KitSummaryScore {
  id: number;
  kit_id: string;
  name: string;
  value: number;
  evaluation_color: Color;
  evaluation_text: string;
}

const fetchKitSummaryScores = async (
  kitId: string,
  name?: string,
): Promise<KitSummaryScore[]> => {
  const queryParams = name ? `?name=${encodeURIComponent(name)}` : '';
  const response: { result: KitSummaryScore[] } = await API.get(
    apiName,
    `${apiBasePath}/kit-summary-scores/${kitId}${queryParams}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.KITS,
        importance: IMPORTANCE.HIGH,
      },
      context: {
        kitId,
        scoreName: name,
      },
    },
  );
  return response?.result ?? [];
};

export const getKitSummaryScoresQuery = (
  kitId: string | null,
  name?: string,
) => ({
  queryKey: ['kitSummaryScores', kitId, name],
  queryFn: () => (kitId ? fetchKitSummaryScores(kitId, name) : []),
  enabled: !!kitId,
});

/**
 * Hook to fetch summary scores for a kit
 * @param kitId - The kit ID to fetch summary scores for
 * @param name - Optional name filter for specific summary score
 * @returns React Query result with summary scores data
 *
 * @example
 * // Get all summary scores for a kit
 * const { data: scores, isLoading } = useKitSummaryScores(kitId);
 *
 * @example
 * // Get specific summary score by name
 * const { data: diversity } = useKitSummaryScores(kitId, 'diversity');
 */
const useKitSummaryScores = (kitId: string | null, name?: string) => {
  return useQuery(getKitSummaryScoresQuery(kitId, name));
};

export default useKitSummaryScores;
